import { AlertOctagon, Check, Clock, CloudOff, Loader2 } from "lucide-react";
import type { ScheduleStatus } from "@/data/types";
import { cn } from "@/lib/cn";
import { formatRelativeAge } from "@/lib/format";

/**
 * Status banner — displays the edge's most recent echo for a SHARED_SCOPE
 * write (schedule or override). Three states the operator cares about:
 *
 *   * "applied"        — the edge confirmed; the doc on disk matches.
 *   * "rejected"       — the edge couldn't ingest something; we show the
 *                        failing entries inline so the operator can fix.
 *   * "pending"        — the local doc is newer than the last echo; the
 *                        edge hasn't ACKed yet (we infer this from the
 *                        `localUpdatedAt` prop).
 *   * "stale heartbeat"— echo is a heartbeat but doesn't reference our
 *                        last save → edge hasn't seen us yet.
 *
 * The banner is compact; it sits in the page header so save loops are
 * visually closed.
 */
export function StatusBanner({
  status,
  localUpdatedAt,
  loading,
  label = "Schedule",
}: {
  status: ScheduleStatus | null;
  /** updatedAt of the last save the SPA made (null if never). */
  localUpdatedAt: number | null;
  loading?: boolean;
  label?: string;
}) {
  if (loading) {
    return (
      <Pill tone="muted" icon={<Loader2 className="h-3 w-3 animate-spin" />}>
        Loading…
      </Pill>
    );
  }
  if (!status) {
    return (
      <Pill tone="muted" icon={<CloudOff className="h-3 w-3" />}>
        {label}: no echo from edge yet
      </Pill>
    );
  }
  // Pending: we saved something newer than the last applied echo.
  if (
    localUpdatedAt !== null &&
    (status.applied_updatedAt ?? 0) < localUpdatedAt
  ) {
    return (
      <Pill tone="warn" icon={<Clock className="h-3 w-3 animate-pulse" />}>
        {label}: waiting for edge to confirm…
      </Pill>
    );
  }
  if (status.echo_kind === "rejected" || status.rejected_entries.length > 0) {
    return (
      <div className="card border-critical/40 bg-critical/5 p-2.5 text-xs">
        <div className="flex items-center gap-1.5 font-semibold text-critical">
          <AlertOctagon className="h-3.5 w-3.5" />
          {label} rejected by edge
        </div>
        <ul className="mt-1 space-y-0.5 pl-5 text-[11px] text-critical/90 list-disc">
          {status.rejected_entries.slice(0, 5).map((r, i) => (
            <li key={i}>
              {r.id ? <code className="font-mono">{r.id}</code> : `entry #${r.index ?? "?"}`}
              <span className="text-text-muted"> — {r.error}</span>
            </li>
          ))}
          {status.error && !status.rejected_entries.length && (
            <li>{status.error}</li>
          )}
        </ul>
      </div>
    );
  }
  return (
    <Pill tone="ok" icon={<Check className="h-3 w-3" />}>
      {label} applied
      {status.applied_entry_count !== null && (
        <span className="text-text-muted"> · {status.applied_entry_count} entr{status.applied_entry_count === 1 ? "y" : "ies"}</span>
      )}
      {status.applied_at_ms && (
        <span className="text-text-faint"> · {formatRelativeAge(Date.now() - status.applied_at_ms)}</span>
      )}
    </Pill>
  );
}

function Pill({
  tone,
  icon,
  children,
}: {
  tone: "ok" | "warn" | "muted" | "critical";
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  const toneCls = {
    ok: "border-ok/30 bg-ok/10 text-ok",
    warn: "border-warn/30 bg-warn/10 text-warn",
    critical: "border-critical/30 bg-critical/10 text-critical",
    muted: "border-edge bg-surface text-text-muted",
  }[tone];
  return (
    <span className={cn(
      "inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-[11px]",
      toneCls,
    )}>
      {icon}
      <span>{children}</span>
    </span>
  );
}
